import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import img1 from "./img/icon1.png";
import img2 from "./img/icon2.png";
import img3 from "./img/icon3.png";

// Data for the cards
const featureData = [
  {
    icon: img1,
    title: "Automate Queries",
    description:
      "Effortlessly automate 80% of common user queries with WhatsApp chatbot.",
    gradient:
      "linear-gradient(180deg, rgb(196, 194, 188) 0%, rgb(187, 186, 183) 100%)",
  },
  {
    icon: img2,
    title: "Drive Sales",
    description: "Automate 3x sales on WhatsApp with Smart WhatsApp Chatbot.",
    gradient:
      "linear-gradient(180deg, rgb(238, 225, 188)0%, rgb(231, 214, 87) 100%)",
  },
  {
    icon: img3,
    title: "Stay Active 24×7",
    description:
      "WhatsApp chatbot works 24/7, responds to user queries and drives sales.",
    gradient:
      "linear-gradient(180deg, rgb(163, 193, 228) 0%, rgb(138, 177, 222) 100%)",
  },
];

const floatAnimations = [
  {
    name: "float1",
    keyframes: `
            0% { transform: translateY(0px); }
            50% { transform: translateY(-18px); }
            100% { transform: translateY(0px); }
        `,
    duration: "4s",
    delay: "0s",
  },
  {
    name: "float2",
    keyframes: `
            0% { transform: translateY(0px); }
            50% { transform: translateY(-28px); }
            100% { transform: translateY(0px); }
        `,
    duration: "5s",
    delay: "0.7s",
  },
  {
    name: "float3",
    keyframes: `
            0% { transform: translateY(0px); }
            50% { transform: translateY(-22px); }
            100% { transform: translateY(0px); }
        `,
    duration: "4.5s",
    delay: "0.3s",
  },
];

// Inject keyframes into the document
if (typeof document !== "undefined") {
  floatAnimations.forEach((anim) => {
    if (!document.getElementById(anim.name)) {
      const style = document.createElement("style");
      style.id = anim.name;
      style.innerHTML = `@keyframes ${anim.name} {${anim.keyframes}}`;
      document.head.appendChild(style);
    }
  });
}

const Card = styled(Box, {
  shouldForwardProp: (prop) => prop !== "gradient" && prop !== "floatIndex",
})(({ gradient, floatIndex = 0 }) => {
  const anim = floatAnimations[floatIndex % floatAnimations.length];
  return {
    background: gradient,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    position: "relative",
    width: "320px",
    height: "450px",
    margin: "30px",
    borderRadius: "20px",
    borderBottomLeftRadius: "160px",
    borderBottomRightRadius: "160px",
    boxShadow:
      "0 15px 0 #fff, inset 0 -15px 0 rgba(255, 255, 255, 0.24), 0 45px 0 rgba(0, 0, 0, 0)",
    overflow: "hidden",
    transition: "all 1s ease-out",
    animation: `${anim.name} ${anim.duration} ease-in-out ${anim.delay} infinite`,
    "&:hover": {
      transform: "scale(1.03)",
    },
    "&:before": {
      content: '""',
      position: "absolute",
      top: "-140px",
      left: "-40%",
      width: "100%",
      height: "120%",
      background:
        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2))",
      transform: "rotate(35deg)",
      pointerEvents: "none",
      filter: "blur(5px)",
    },
  };
});

const IconContainer = styled(Box)({
  position: "relative",
  width: "140px",
  height: "120px",
  background: "#c5e3f5",
  borderBottomLeftRadius: "100px",
  borderBottomRightRadius: "100px",
  boxShadow: "0 15px 0 rgba(0, 0, 0, 0.1), inset 0 -8px 0 #fff",
  zIndex: 100,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-50px",
    width: "50px",
    height: "50px",
    background: "transparent",
    borderTopRightRadius: "50px",
    boxShadow: "15px -15px 0 15px #c5e3f5",
  },
  "&:after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: "-50px",
    width: "50px",
    height: "50px",
    background: "transparent",
    borderTopLeftRadius: "50px",
    boxShadow: "-15px -15px 0 15px #c5e3f5",
  },
});

const Content = styled(Box)({
  position: "absolute",
  width: "100%",
  padding: "30px",
  paddingTop: "150px",
  textAlign: "center",
  top: "10%",
});

const TextSection = () => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#c5e3f5",
      }}
    >
      <Box
        sx={{
          width: { xl: "74%", lg: "79%", md: "90%", sm: "90%" },
          p: 4,
        }}
      >
        {/* Title */}
        <Typography
          variant="h3"
          fontWeight="bold"
          gutterBottom
          sx={{
            fontSize: { xs: "2.3rem", md: "2.8rem" },
            textAlign: "center",
            background: "",
            mb: 4,
          }}
        >
          Build Smart{" "}
          <span style={{ color: "#00C851" }}>WhatsApp Chatbots</span> with
          Vertex Suite
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          {featureData.map((card, index) => (
            <Card key={index} gradient={card.gradient}>
              <IconContainer>
                <img src={card.icon} alt="icon" />
              </IconContainer>
              <Content>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: "1.8em",
                    color: "text.primary",
                    mb: "15px",
                    fontWeight: 600,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: "1.5em",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  {card.description}
                </Typography>
              </Content>
            </Card>
          ))}
        </Box>

        <Box
          textAlign={"center"}
          sx={{
            py: 4,
          }}
        >
          {/* Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            gutterBottom
            sx={{ fontSize: { xs: "2.3rem", md: "2.8rem" } }}
          >
            Add-on Feature
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: "#616161",
              px: { xs: 3, md: 3 }, // equal padding left and right for spacing
              mb: 4,
              fontSize: "1rem",
              lineHeight: 1.6,
            }}
          >
            Unlock our advanced drag-and-drop Flow Builder for just ₹1999/month
            and start automating support, lead qualificationand product guidance
            instantly.
          </Typography>

          {/* Button */}
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: "#00C851",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1.5,
              "&:hover": {
                backgroundColor: "#00b34d",
              },
            }}
          >
            Explore Plans And Pricing
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TextSection;
